import { config } from 'lib-shared-backend-config';
import { searchParamsToURLSearchParams } from 'lib-shared-url';
import { authManager } from '../authManager';
/** Gets the base query for making api requests with axios. */
export function axiosBaseQuery(axiosInstance) {
    return async (endpointOptions, _api, extraOptions) => {
        if (extraOptions?.mockResponse) {
            const { data, delay = 0 } = extraOptions.mockResponse;
            return new Promise((resolve) => setTimeout(() => resolve({ data }), delay));
        }
        const axiosConfig = await createAxiosConfig(endpointOptions);
        try {
            const { data, ...response } = await axiosInstance(axiosConfig);
            return { data, meta: { response } };
        }
        catch (error) {
            const axiosError = error;
            const { response } = axiosError;
            return {
                error: {
                    status: response?.status,
                    data: response?.data,
                },
            };
        }
    };
}
/**
 * Creates an axios request config.
 *
 * @param endpointOptions The endpoint options.
 */
export async function createAxiosConfig(endpointOptions) {
    const { auth, body, contentType, headers, method, params, path, type, version, timeout, } = parseEndpointOptions(endpointOptions);
    const baseUrl = config.url[type ?? 'rest'];
    const url = isPaginatedEndpointOptions(endpointOptions)
        ? [path]
        : [version ? `v${await version}` : undefined, path];
    return {
        url: [
            baseUrl,
            ...url
                .filter(Boolean)
                .map((part) => part.replace(/^\/|\/$/g, '')),
        ].join('/'),
        ...(method ? { method } : {}),
        ...(params ? { params: searchParamsToURLSearchParams(params) } : {}),
        ...(body ? { data: body } : {}),
        timeout,
        headers: {
            ['Content-Type']: contentType ?? 'application/json',
            ...((typeof auth === 'undefined' || auth) && {
                ['Authorization']: await createAuthorizationHeaderValue(),
            }),
            ...headers,
        },
        validateStatus: (status) => status >= 200 && status < 300,
    };
}
/** Creates the authorization header value. */
export async function createAuthorizationHeaderValue() {
    const authData = await authManager.getAuthData();
    const token = authData?.access_token;
    const tokenBase64 = token ? btoa(`token:${token}`) : '';
    return `Basic ${tokenBase64}`;
}
/**
 * Checks whether or not the error is a query error.
 *
 * @param error The error.
 */
export function isQueryError(error) {
    return error?.data !== undefined;
}
/**
 * Checks whether or not the error is a serialized error.
 *
 * @param error The error.
 */
export function isSerializedError(error) {
    return error?.stack !== undefined;
}
/**
 * Checks whether or not the error is a FetchBaseQueryError.
 *
 * @param error The error.
 */
export function isFetchBaseQueryError(error) {
    return typeof error === 'object' && error != null && 'status' in error;
}
/**
 * Checks whether or not the response is with data rather than with error. This
 * format of response is received by dispatched mutations.
 *
 * @param response The rtk-q mutation api response.
 */
export function isResponseWithData(response) {
    return (response.error === undefined);
}
/**
 * Gets an {@link AsyncStatus} from RTK query states.
 *
 * @param states The query states.
 */
export function getAsyncStatusFromRtkQueryStates({ isError, isSuccess, isFetching, isUninitialized, }) {
    if (isUninitialized)
        return 'uninitialized';
    if (isError)
        return 'rejected';
    if (isSuccess && !isFetching)
        return 'fulfilled';
    return 'pending';
}
/**
 * Gets an {@link AsyncStatus} from RTK mutation states.
 *
 * @param states The mutation states.
 */
export function getAsyncStatusFromRtkMutationStates({ isError, isSuccess, isUninitialized, }) {
    if (isUninitialized)
        return 'uninitialized';
    if (isError)
        return 'rejected';
    if (isSuccess)
        return 'fulfilled';
    return 'pending';
}
/**
 * Gets the aggregated async status based on the async status of other async
 * status.
 *
 * @param asyncStatus The list of async status.
 */
export function getAsyncStatus(asyncStatus) {
    if (asyncStatus.some((asyncStatus) => asyncStatus === 'pending')) {
        return 'pending';
    }
    if (asyncStatus.some((asyncStatus) => asyncStatus === 'rejected')) {
        return 'rejected';
    }
    if (asyncStatus.every((asyncStatus) => asyncStatus === 'fulfilled')) {
        return 'fulfilled';
    }
    return 'uninitialized';
}
/**
 * Parses the endpoint options by either returning them as-is or creating them
 * from a string.
 *
 * @param endpointOptions The endpoint options.
 */
function parseEndpointOptions(endpointOptions) {
    return isPaginatedEndpointOptions(endpointOptions)
        ? { path: endpointOptions }
        : endpointOptions;
}
/**
 * Checks whether or not the endpoint options are the paginated variant.
 *
 * @param endpointOptions The endpoint options.
 */
function isPaginatedEndpointOptions(endpointOptions) {
    return typeof endpointOptions === 'string';
}
//# sourceMappingURL=axiosBaseQuery.js.map